import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
// ScrollSmoother requires ScrollTrigger
import { ScrollSmoother } from 'gsap/ScrollSmoother.js';

/** Componsante SCroller de TimTools */
export default class Scroller {
  /**
   * Méthode constructeur
   * @param {HTMLElement} element - Élément HTML sur lequel la composante est instanciée
   */
  constructor(element) {
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother);
    this.element = element;
    this.scroller;
    this.hasSkew = false;
    this.isAnchorFocus = false;

    this.setOptions();
    this.init();
  }

  init() {
    this.scroller = ScrollSmoother.create({
      smooth: 2,
      effects: true,
      smoothTouch: 0.1,
      onUpdate: this.onUpdate.bind(this),
      onStop: this.onStop.bind(this),
      onFocusIn: () => !this.isAnchorFocus,
      ease: 'expo.out',
    });
    this.initAnchorLinks();
  }

  initAnchorLinks() {
    if (this.onAnchorClick)
      document.removeEventListener('click', this.onAnchorClick);
    this.onAnchorClick = this.scrollToAnchor.bind(this);
    document.addEventListener('click', this.onAnchorClick);
  }

  scrollToAnchor(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href^="#"]');
    if (!link || link.hasAttribute('download')) return;
    if (link.target && link.target !== '_self') return;

    const hash = link.getAttribute('href');
    if (hash === '#') return;

    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return;
    }

    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)')
      .matches;
    this.scroller.scrollTo(target, smooth, 'top top');

    // Déplace aussi le focus pour la navigation au clavier, sans scroll natif.
    if (target instanceof HTMLElement) {
      const hadTabindex = target.hasAttribute('tabindex');
      if (!hadTabindex) target.setAttribute('tabindex', '-1');
      // ScrollSmoother recentre les éléments au focus, même avec preventScroll.
      this.isAnchorFocus = true;
      try {
        target.focus({ preventScroll: true });
      } finally {
        this.isAnchorFocus = false;
        if (!hadTabindex) target.removeAttribute('tabindex');
      }
    }
  }

  onUpdate(self) {
    if (this.hasSkew) this.updateSkew(self);
  }
  onStop(self) {
    if (this.hasSkew) this.stopSkew(self);
  }

  setOptions() {
    if (this.element.querySelector('.js-horiz')) {
      this.initHorizSections();
    }

    if ('skew' in this.element.dataset) {
      this.initSkew();
    }
    if ('pinItems' in this.element.dataset) {
      this.initPins();
    }
  }

  initSkew() {
    // Les légendes des événements doivent suivre les transformations de leur image.
    const targets = this.element.querySelectorAll(
      'img:not(.event__media img), .event__media figure',
    );
    this.skewSetter = gsap.quickTo(targets, 'skewY');
    this.scaleSetter = gsap.quickTo(targets, 'scaleY');
    this.hasSkew = true;
  }
  updateSkew(self) {
    const velocity = self.getVelocity();
    let skew = gsap.utils.mapRange(-1000, 1000, -5, 5, velocity);
    // Empêcher d’aller trop loin
    skew = gsap.utils.clamp(-5, 5, skew);

    let scale = velocity / 1000;
    scale = 1 + scale;
    scale = gsap.utils.clamp(0.9, 1.1, scale);

    this.skewSetter(skew);
    this.scaleSetter(scale);
  }
  stopSkew() {
    this.skewSetter(0);
    this.scaleSetter(1);
  }
  initPins() {
    this.pinnedItems = this.element.querySelectorAll('.js-pinned');
    for (let i = 0; i < this.pinnedItems.length; i++) {
      const element = this.pinnedItems[i];

      ScrollTrigger.create({
        pin: element,
        trigger: element.parentElement,
        start: 'center center',
        end: '80% center',
      });
    }
  }
  initHorizSections() {
    // TODO faire boucle pour plusieurs
    const buffer = 200;
    const sectionHoriz = this.element.querySelector('.js-horiz');

    const pannels = sectionHoriz.querySelectorAll('.js-panel');
    const nbPannels = pannels.length - 1;

    gsap.to(pannels, {
      xPercent: -100 * nbPannels,
      ease: 'none',
      scrollTrigger: {
        trigger: sectionHoriz,
        pin: true,
        scrub: 1,
        snap: 1 / nbPannels,
        end: () => '+=' + (sectionHoriz.offsetWidth + buffer),
      },
    });
  }
}
